// useAltivModal — comportamientos comunes para modales con `altiv-modal-shell`.
//
// El proyecto no tiene un componente <AltivModal> wrapper (cada modal monta
// el shell inline). Para evitar refactor masivo, este hook centraliza dos
// comportamientos que faltaban a la mayoría de modales:
//
//   1. Escape para cerrar — bug 12 audit ops manager 2026-05-25. UX estándar
//      espera Esc=close en cualquier modal; antes solo cerraban con click en X.
//
//   2. Body scroll lock mientras el modal está abierto — evita el scroll
//      "fantasma" del fondo cuando se monta un modal mid-page. Idempotente:
//      mantiene un contador de modales abiertos para anidados.
//
// Bug 13 (pointer-events bloqueando tras cerrar) se evita con el cleanup
// estricto del useEffect: si el modal desmonta sin que onClose corra (ej.
// padre re-renderea sin él), el listener se remueve igual.

import { useEffect } from "react";

let _bodyLockCount = 0;
// Scroll position captured when the FIRST modal locked the body. Restored when
// the LAST modal unlocks — without this the page jumps to the top on close,
// because some browsers reset the document scroll to 0 while the scrolling
// element has `overflow: hidden`. Preserving it keeps the user where they were
// (e.g. closing a trip-detail modal returns to the same calendar scroll spot).
let _savedScrollY = 0;

function lockBodyScroll() {
  _bodyLockCount += 1;
  if (_bodyLockCount === 1) {
    _savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.overflow = "hidden";
    // Mirror the lock as a data attribute on <html> so CSS can target it
    // with a simple attribute selector instead of `html:has(.altiv-modal-shell)`.
    // The :has() variant invalidated on every DOM mutation inside the modal,
    // which made scrolling / typing visibly heavier.
    document.documentElement.dataset.altivModalOpen = "true";
  }
}

function unlockBodyScroll() {
  _bodyLockCount = Math.max(0, _bodyLockCount - 1);
  if (_bodyLockCount === 0) {
    document.body.style.overflow = "";
    delete document.documentElement.dataset.altivModalOpen;
    // Restore the pre-lock scroll position. No-op when the browser already
    // kept it; corrective when it didn't. Instant (not smooth) so there's no
    // visible jump-then-scroll.
    window.scrollTo(0, _savedScrollY);
  }
}

/**
 * @param {object}   opts
 * @param {boolean}  opts.open      — Si el modal está montado/visible.
 * @param {function} opts.onClose   — Handler para cerrar (se llama con Escape).
 * @param {boolean}  [opts.lockBody=true] — Bloquear scroll del body mientras abierto.
 */
export function useAltivModal({ open, onClose, lockBody = true }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKey);

    if (lockBody) lockBodyScroll();

    return () => {
      document.removeEventListener("keydown", handleKey);
      if (lockBody) unlockBodyScroll();
    };
  }, [open, onClose, lockBody]);
}
